import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';

// Function to create circular texture for stars
const createCircleTexture = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const context = canvas.getContext('2d');
  
  const gradient = context.createRadialGradient(32, 32, 0, 32, 32, 32);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
  gradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.8)');
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
  
  context.fillStyle = gradient;
  context.fillRect(0, 0, 64, 64);
  
  const texture = new THREE.CanvasTexture(canvas);
  return texture;
};

// Function to create glowing texture for constellation points
const createGlowTexture = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const context = canvas.getContext('2d');
  
  // Create a more complex glow effect
  const gradient = context.createRadialGradient(64, 64, 0, 64, 64, 64);
  gradient.addColorStop(0, 'rgba(135, 206, 250, 1)'); // Light blue center
  gradient.addColorStop(0.1, 'rgba(100, 149, 237, 0.9)'); // Cornflower blue
  gradient.addColorStop(0.3, 'rgba(70, 130, 180, 0.6)'); // Steel blue
  gradient.addColorStop(0.6, 'rgba(25, 25, 112, 0.3)'); // Midnight blue
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
  
  context.fillStyle = gradient;
  context.fillRect(0, 0, 128, 128);
  
  // Add a bright core
  const coreGradient = context.createRadialGradient(64, 64, 0, 64, 64, 20);
  coreGradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
  coreGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
  
  context.fillStyle = coreGradient;
  context.fillRect(0, 0, 128, 128);
  
  const texture = new THREE.CanvasTexture(canvas);
  return texture;
};

const App = () => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const animationRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 1);
    mountRef.current.appendChild(renderer.domElement);

    // Stars
    const starsGeometry = new THREE.BufferGeometry();
    const starsCount = 1000;
    const starsPositions = new Float32Array(starsCount * 3);
    const starsColors = new Float32Array(starsCount * 3);
    const starsSizes = new Float32Array(starsCount);

    // Store original colors for twinkling
    const originalColors = new Float32Array(starsCount * 3);

    for (let i = 0; i < starsCount; i++) {
      const i3 = i * 3;
      
      // Position
      starsPositions[i3] = (Math.random() - 0.5) * 2000;
      starsPositions[i3 + 1] = (Math.random() - 0.5) * 2000;
      starsPositions[i3 + 2] = (Math.random() - 0.5) * 2000;
      
      // Color variations (white to blue)
      const colorIntensity = Math.random() * 0.5 + 0.5;
      const r = colorIntensity;
      const g = colorIntensity;
      const b = Math.min(1, colorIntensity + Math.random() * 0.3);
      
      starsColors[i3] = r;
      starsColors[i3 + 1] = g;
      starsColors[i3 + 2] = b;
      
      originalColors[i3] = r;
      originalColors[i3 + 1] = g;
      originalColors[i3 + 2] = b;
      
      // Size
      starsSizes[i] = Math.random() * 3 + 1;
    }

    starsGeometry.setAttribute('position', new THREE.BufferAttribute(starsPositions, 3));
    starsGeometry.setAttribute('color', new THREE.BufferAttribute(starsColors, 3));
    starsGeometry.setAttribute('size', new THREE.BufferAttribute(starsSizes, 1));

    // Custom shader for circular points
    const starsMaterial = new THREE.PointsMaterial({
      size: 2,
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      map: createCircleTexture()
    });

    const stars = new THREE.Points(starsGeometry, starsMaterial);
    scene.add(stars);

    // Constellation points (brighter, larger stars)
    const constellationGeometry = new THREE.BufferGeometry();
    const constellationCount = 50;
    const constellationPositions = new Float32Array(constellationCount * 3);
    const constellationColors = new Float32Array(constellationCount * 3);
    const originalConstellationPositions = new Float32Array(constellationCount * 3);

    for (let i = 0; i < constellationCount; i++) {
      const i3 = i * 3;
      
      // Position in a more organized pattern
      const angle = (i / constellationCount) * Math.PI * 2;
      const radius = Math.random() * 300 + 100;
      const height = (Math.random() - 0.5) * 200;
      
      const x = Math.cos(angle) * radius + (Math.random() - 0.5) * 100;
      const y = height;
      const z = Math.sin(angle) * radius + (Math.random() - 0.5) * 100;
      
      constellationPositions[i3] = x;
      constellationPositions[i3 + 1] = y;
      constellationPositions[i3 + 2] = z;
      
      originalConstellationPositions[i3] = x;
      originalConstellationPositions[i3 + 1] = y;
      originalConstellationPositions[i3 + 2] = z;
      
      // Brighter colors
      constellationColors[i3] = 0.8 + Math.random() * 0.2;
      constellationColors[i3 + 1] = 0.9 + Math.random() * 0.1;
      constellationColors[i3 + 2] = 1;
    }

    constellationGeometry.setAttribute('position', new THREE.BufferAttribute(constellationPositions, 3));
    constellationGeometry.setAttribute('color', new THREE.BufferAttribute(constellationColors, 3));

    const constellationMaterial = new THREE.PointsMaterial({
      size: 8,
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
      map: createGlowTexture()
    });

    const constellation = new THREE.Points(constellationGeometry, constellationMaterial);
    scene.add(constellation);

    // Connection lines between constellation points
    const linesGeometry = new THREE.BufferGeometry();
    const linePositions = [];
    const lineColors = [];

    // Create connections between nearby constellation points
    for (let i = 0; i < constellationCount; i++) {
      for (let j = i + 1; j < constellationCount; j++) {
        const pos1 = new THREE.Vector3(
          constellationPositions[i * 3],
          constellationPositions[i * 3 + 1],
          constellationPositions[i * 3 + 2]
        );
        const pos2 = new THREE.Vector3(
          constellationPositions[j * 3],
          constellationPositions[j * 3 + 1],
          constellationPositions[j * 3 + 2]
        );
        
        const distance = pos1.distanceTo(pos2);
        
        // Only connect nearby points
        if (distance < 150 && Math.random() > 0.7) {
          linePositions.push(pos1.x, pos1.y, pos1.z);
          linePositions.push(pos2.x, pos2.y, pos2.z);
          
          // Gradient effect on lines
          const opacity = 1 - (distance / 150) * 0.5;
          lineColors.push(0.4, 0.6, 1, opacity);
          lineColors.push(0.4, 0.6, 1, opacity);
        }
      }
    }

    linesGeometry.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
    linesGeometry.setAttribute('color', new THREE.Float32BufferAttribute(lineColors, 4));

    const linesMaterial = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.3,
      blending: THREE.AdditiveBlending
    });

    const lines = new THREE.LineSegments(linesGeometry, linesMaterial);
    scene.add(lines);

    // Camera position
    camera.position.set(0, 50, 300);

    // Mouse interaction
    const handleMouseMove = (event) => {
      mouseRef.current.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouseRef.current.y = -(event.clientY / window.innerHeight) * 2 + 1;
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Animation
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      
      const time = Date.now() * 0.001;
      
      // Rotate the entire scene slowly
      scene.rotation.y += 0.0005;
      scene.rotation.x += 0.0005;
      scene.rotation.y += 0.005;
      
      // Mouse interaction - amplify the effect and make it smoother
      const targetX = mouseRef.current.x * 200;
      const targetY = -mouseRef.current.y * 200;
      
      camera.position.x += (targetX - camera.position.x) * 0.02;
      camera.position.y += (targetY + 50 - camera.position.y) * 0.02;
      camera.lookAt(scene.position);
      
      // Animate constellation points
      const positions = constellation.geometry.attributes.position.array;
      
      for (let i = 0; i < constellationCount; i++) {
        const i3 = i * 3;
        positions[i3 + 1] = originalConstellationPositions[i3 + 1] + Math.sin(time + i) * 0.5;
      }
      constellation.geometry.attributes.position.needsUpdate = true;
      
      // Twinkle effect - corrected
      const colors = stars.geometry.attributes.color.array;
      for (let i = 0; i < starsCount; i++) {
        const i3 = i * 3;
        const twinkle = Math.sin(time * 2 + i) * 0.2 + 0.8;
        colors[i3] = originalColors[i3] * twinkle;
        colors[i3 + 1] = originalColors[i3 + 1] * twinkle;
        colors[i3 + 2] = originalColors[i3 + 2] * twinkle;
      }
      stars.geometry.attributes.color.needsUpdate = true;
      
      renderer.render(scene, camera);
    };

    // Handle resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    // Start animation
    animate();
    setIsLoaded(true);
    sceneRef.current = {
    scene,
    camera,
    renderer,
    cleanup: () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    }
  };

  return () => {
    if (sceneRef.current && process.env.NODE_ENV !== 'development') {
      sceneRef.current.cleanup();
    }
  };
  }, []);

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black">
      <div ref={mountRef} className="absolute inset-0" />
      
      {/* Loading overlay */}
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="text-white text-xl font-light tracking-wider animate-pulse">
            Initializing constellation...
          </div>
        </div>
      )}
      
      {/* Subtle overlay text */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-8 left-8 text-white text-sm font-light tracking-wider opacity-30">
          Hi :)
        </div>
        
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center pointer-events-auto">
          <div className="text-white text-4xl md:text-6xl font-light tracking-widest mb-4 opacity-80">
            {/* ∞ */}
          </div>
          <div className="text-white text-lg md:text-xl font-light tracking-wider opacity-60">
            {/* Hi ! */}
          </div>
        </div>
      </div>  
    </div>

  );
};

export default App;